/*
 * Copyright (c) 2015 2017, Massachusetts Institute of Technology (MIT)
 * All rights reserved.
 * ====================================================================
 * This material may be reproduced by or for the
 * U.S. Government pursuant to the copyright license
 * under the clause at DFARS 252.227 7013 (June, 1995).
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions
 * are met:
 *
 * 1. Redistributions of source code must retain the above copyright
 * notice, this list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright
 * notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 * contributors may be used to endorse or promote products derived
 * from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
 * HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * ====================================================================
 */
define(() => {
  'use strict';
  
  const ColorModule = function() {};

  /**
   * Creates the Color module and initializes all of the
   * colors used in this project.
   */

  ColorModule.prototype.init = function() {
    this.BLACK = '#000000';
    this.WHITE = '#FFFFFF';
    this.RED = '#FF0000';
    this.LIGHTRED = '#FFA8A8';
    this.DARKRED = '#660000';
    this.BLUE = '#0024FA';
    this.LIGHTBLUE = '#9ACDFF';
    this.BABYBLUE = '#80DEEA';
    this.YELLOW = '#FFFF33';
    this.LIGHTYELLOW = '#FFFF00';
    this.GREEN = '#00B200';
    this.LIMEGREEN = '#00FF00';
    this.DARKGREEN = '#006600';
    this.ORANGE = '#FF8000';
    this.ORANGEYELLOW = '#FFCC00';
    this.DARKORANGE = '#CC6600';
    this.PURPLE = '#6600FF';
    this.GREY = '#CCCCCC';
    this.DARKGREYBLUE = '#546E7A';
    this.GREYBLUE = '#DFE8F6';
    this.GREYYELLOW = '#FFEE58';
    this.GREYGREEN = '#7CB342';
    this.LIGHTGREY = '#E0E0E0';
    this.LIGHTLIGHTGREY = '#FAFAFA';
    this.LIGHTGREYBLUE = '#BBBBFF';
    this.LIGHTGREYGREEN = '#BBFFBB';
    this.DARKGREY = '#777777';
    this.DARKDARKGREY = '#333333';
    this.DARKGREYBLUE = '#8CC8F0';
    this.VIOLET = '#660066';
    this.PINKRED = '#EF5350';
    this.WINDPROBRED = '#C00000';
    this.WINDPROBDARKRED = '#800000';
    this.WINDPROBPURPLE = '#6600CC';
    this.BLANDYELLOW = '#FFCC66';

    // P-Surge colors
    this.PSURGE_3 = '#000080';
    this.PSURGE_5 = '#0000FF';
    this.PSURGE_7 = '#8080FF';
    this.PSURGE_9 = '#C8FFAF';
    this.PSURGE_11 = '#008000';
    this.PSURGE_13 = '#00CD00';
    this.PSURGE_15 = '#80FF00';
    this.PSURGE_17 = '#FFFF00';
    this.PSURGE_19 = '#FFD700';
    this.PSURGE_21 = '#D78500';
    this.PSURGE_23 = '#FF8000';
    this.PSURGE_25 = '#FF0000';
    this.PSURGE_27 = '#C80000';
    this.PSURGE_29 = '#8B008B';
    this.PSURGE_31 = '#FF00FF';

    this.RGBBLACK = this.getRGBFromHex(this.BLACK);
    this.RGBRED = this.getRGBFromHex(this.RED);
    this.RGBLIGHTRED = this.getRGBFromHex(this.LIGHTRED);
    this.RGBDARKRED = this.getRGBFromHex(this.DARKRED);
    this.RGBBLUE = this.getRGBFromHex(this.BLUE);
    this.RGBLIGHTBLUE = this.getRGBFromHex(this.LIGHTBLUE);
    this.RGBBABYBLUE = this.getRGBFromHex(this.BABYBLUE);
    this.RGBYELLOW = this.getRGBFromHex(this.YELLOW);
    this.RGBGREEN = this.getRGBFromHex(this.GREEN);
    this.RGBLIMEGREEN = this.getRGBFromHex(this.LIMEGREEN);
    this.RGBDARKGREEN = this.getRGBFromHex(this.DARKGREEN);
    this.RGBORANGE = this.getRGBFromHex(this.ORANGE);
    this.RGBORANGEYELLOW = this.getRGBFromHex(this.ORANGEYELLOW);
    this.RGBDARKORANGE = this.getRGBFromHex(this.DARKORANGE);
    this.RGBPURPLE = this.getRGBFromHex(this.PURPLE);
    this.RGPPINKRED = this.getRGBFromHex(this.PINKRED);
    this.RGBGREY = this.getRGBFromHex(this.GREY);
    this.RGBDARKGREY = this.getRGBFromHex(this.DARKGREY);
    this.RGBDARKGREYBLUE = this.getRGBFromHex(this.DARKGREYBLUE);
    this.RGBGREYBLUE = this.getRGBFromHex(this.GREYBLUE);
    this.RGBGREYGREEN = this.getRGBFromHex(this.GREYGREEN);
    this.RGBGREYYELLOW = this.getRGBFromHex(this.GREYYELLOW);
    this.RGBDARKGREYBLUE = this.getRGBFromHex(this.DARKGREYBLUE);
    this.RGBLIGHTGREY = this.getRGBFromHex(this.LIGHTGREY);
    this.RGBLIGHTGREYGREEN = this.getRGBFromHex(this.LIGHTGREYGREEN);
    this.RGBLIGHTGREYBLUE = this.getRGBFromHex(this.LIGHTGREYBLUE);
    this.RGBLIGHTLIGHTGREY = this.getRGBFromHex(this.LIGHTLIGHTGREY);
    this.RGBVIOLET = this.getRGBFromHex(this.VIOLET);
    this.RGBWHITE = this.getRGBFromHex(this.WHITE);
    this.RGBLIGHTYELLOW = this.getRGBFromHex(this.LIGHTYELLOW);
    this.RGBWINDPROBRED = this.getRGBFromHex(this.WINDPROBRED);
    this.RGBWINDPROBDARKRED = this.getRGBFromHex(this.WINDPROBDARKRED);
    this.RGBWINDPROBPURPLE = this.getRGBFromHex(this.WINDPROBPURPLE);
    this.RGBBLANDYELLOW = this.getRGBFromHex(this.BLANDYELLOW);
  };

  /**
     * Converts the color hex value into an instance of rgb or 
     * a string version of rgb.
     *
     * @param {(string)} hex - The hex string being sent to convert.
     * @param {(boolean)} string - Boolean that if set to true returns a string representation of the rgb object.
     */

  ColorModule.prototype.getRGBFromHex = function(hex, string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (string === true) {
      return result 
      ? `rgb(${  parseInt(result[1], 16)  },${ 
          parseInt(result[2], 16)  },${  
          parseInt(result[3], 16)  })`
      : null;
    } else {
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;    
    }
  };
  
  return new ColorModule();
});
